import React, { useRef, useState, useEffect } from 'react';

interface CustomAnimatedChartProps {
  startDate: Date;
  duration?: number;
}

const AnimatedChart: React.FC<CustomAnimatedChartProps> = ({ startDate, duration = 3000 }) => {
  const pathRef = useRef<SVGPathElement | null>(null);
  const [movingPoint, setMovingPoint] = useState<{ x: number; y: number } | null>(null);
  const [dateLabel, setDateLabel] = useState<string>("");

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    const totalLength = path.getTotalLength();
    const totalMonths = 12;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const startDt = new Date(startDate);
    const startDay = startDt.getDate();

    // Define the start and end fractions for the animation (20% to 80% of the path)
    const startFraction = 0.2;
    const endFraction = 0.9;

    const startTime = performance.now();

    const animate = (time: number) => {
      const elapsed = time - startTime;
      let progress = elapsed / duration;
      if (progress > 1) progress = 1;

      // Calculate effective progress in the range [startFraction, endFraction]
      const effectiveProgress = startFraction + (endFraction - startFraction) * progress;
      const pt = path.getPointAtLength(totalLength * effectiveProgress);
      setMovingPoint({ x: pt.x, y: pt.y });

      // Map effective progress to the full date range (0 corresponds to startDate, 1 – startDate + 12 months)
      const fractionForDate = (effectiveProgress - startFraction) / (endFraction - startFraction);
      const currentMonthIndex = Math.floor(fractionForDate * totalMonths);
      const totalMonthsFromStart = startDt.getMonth() + currentMonthIndex;
      const computedYear = startDt.getFullYear() + Math.floor(totalMonthsFromStart / 12);
      const computedMonth = totalMonthsFromStart % 12;
      setDateLabel(`${monthNames[computedMonth]} ${startDay}, ${computedYear}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [startDate, duration]);

  return (
    <div className="w-full mx-auto">
      <svg viewBox="0 0 550 400" className="w-full h-auto">
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#40336E" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#40336E" stopOpacity="0" />
          </linearGradient>
          {/* Clip path to restrict the gradient fill and the vertical line */}
          <clipPath id="clipGradient">
            {/*
              The rectangle width is set to movingPoint.x, so it updates dynamically.
              This ensures that any element using this clipPath will be visible only
              to the left of the moving circle.
            */}
            <rect x="0" y="0" width={movingPoint ? movingPoint.x : 0} height="400" />
          </clipPath>
        </defs>

        {/* Area fill path wrapped with clipPath */}
        <path
          d="M0,100 C150,250 400,300 550,300 L550,400 L0,400 Z"
          fill="url(#areaGradient)"
          clipPath="url(#clipGradient)"
        />

        {/* Main animated path */}
        <path
          ref={pathRef}
          d="M0,100 C150,250 400,300 550,300"
          stroke="#40336E"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />

        {movingPoint && (
          <>
            <line
              x1={movingPoint.x}
              y1="100"
              x2={movingPoint.x}
              y2="400"
              stroke="#A6A6A6"
              strokeWidth="1"
              clipPath="url(#clipGradient)"
            />
            {/* Shadow circle */}
            <circle
              cx={movingPoint.x}
              cy={movingPoint.y}
              r="40"
              fill="#40336E"
              opacity="0.1"
            />
            {/* Main circle */}
            <circle
              cx={movingPoint.x}
              cy={movingPoint.y}
              r="10"
              fill="#40336E"
            />
            {/* Inner white circle */}
            <circle
              cx={movingPoint.x}
              cy={movingPoint.y}
              r="4"
              fill="#FFFFFF"
            />
          </>
        )}

        {movingPoint && dateLabel && (
          <text
            x={movingPoint.x}
            y="80"
            fill="#303030"
            fontSize="16"
            fontWeight="700"
            textAnchor="middle"
          >
            {dateLabel}
          </text>
        )}
      </svg>
    </div>
  );
};

export default AnimatedChart;
